import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Home, MessageSquare, Calendar, Church, Handshake,
  Megaphone, ClipboardList, Inbox, Users, Settings, LogOut, ExternalLink, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { checkSupabaseReady, isLive } from "@/services/dataClient";
import { LOGO_URL } from "@/data/seed/siteContent";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/home-sections", label: "Pages & Home Sections", icon: Home },
  { to: "/admin/forums", label: "Forums", icon: MessageSquare },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/units", label: "Units", icon: Church },
  { to: "/admin/sponsors", label: "Sponsors", icon: Handshake },
  { to: "/admin/banners", label: "Banners / Ads", icon: Megaphone },
  { to: "/admin/forms", label: "Forms", icon: ClipboardList },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/media", label: "Media Library", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, isDemo, signOut } = useAuth();
  const navigate = useNavigate();
  const [live, setLive] = useState(true);

  useEffect(() => {
    checkSupabaseReady().then(() => setLive(isLive()));
  }, []);

  async function handleSignOut() {
    await signOut();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen bg-cream-alt flex">
      {/* Sidebar */}
      <aside className="w-64 bg-maroon text-white flex-col hidden md:flex shrink-0 sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <img src={LOGO_URL} alt="BCS" className="h-10 w-10 rounded-full bg-white/95 p-0.5" />
          <div className="leading-tight">
            <p className="font-serif text-base font-semibold">BCS Admin</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold-light">Content Studio</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-white/15 text-white font-medium" : "text-white/70 hover:text-white hover:bg-white/8"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white transition-colors">
            <ExternalLink size={16} /> View public site
          </a>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/8 transition-colors"
            data-testid="admin-signout-btn"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4 sticky top-0 z-30">
          <p className="text-sm text-ink-muted truncate">
            Signed in as <span className="font-medium text-ink">{user?.email}</span>
          </p>
          <div className="flex items-center gap-3">
            {isDemo && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="demo-mode-badge">
                <AlertTriangle size={12} /> Demo mode
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-full px-3 py-1 border ${
                live ? "text-green-800 bg-green-50 border-green-200" : "text-amber-800 bg-amber-50 border-amber-200"
              }`}
              data-testid="supabase-status-badge"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-green-600" : "bg-amber-500"}`} />
              {live ? "Database connected" : "Seed data (run schema.sql)"}
            </span>
          </div>
        </header>
        {!live && (
          <div className="bg-amber-50 border-b border-amber-200 px-5 sm:px-8 py-2.5 text-xs text-amber-900" data-testid="supabase-setup-notice">
            Supabase tables not found — the dashboard is running on local seed data. Run{" "}
            <code className="font-mono bg-amber-100 px-1 rounded">supabase/schema.sql</code> and{" "}
            <code className="font-mono bg-amber-100 px-1 rounded">seed.sql</code> in your Supabase SQL Editor to go live.
            Edits made now are stored locally in this browser.
          </div>
        )}
        <main className="flex-1 p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
